/* eslint-disable no-async-promise-executor */
/* eslint-disable @typescript-eslint/indent */
import React, { useContext, useEffect, useState } from 'react';
import { getDateWithSlashes, getTimeAsNumber } from '@helpers/date';
import Product from '@customTypes/product';
import { ProductDetailsProps } from '@customTypes/product-details';
import { store } from '@context/store';
import toggleErrorPopup from '@helpers/toggleErrorPopup';
import { addToCart as addToCartEvent, addonSelected } from '@events/DLP';
import { useCookies } from 'react-cookie';
import { ShouldShowFairPricingFromHotelDetails } from '@utils/fairPricing';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import User from '@customTypes/user';
import actionTypes from '@context/actionTypes';
import useAddOns from '@hooks/useAddons';
import useAddToCart from '@hooks/useAddToCart';
import Addons from '@components/common/Addons';
import useAlgoliaEvents from '@hooks/useAlgoliaEvents';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import ImageLoaded from '@components/common/ImageLoaded/ImageLoaded';
import { getPhotoUrlFromProduct } from '@components/ProductDetails/ProductComponentWithImage';
import { TagIcon as TagIconSolar } from '@resortpass/solar-icons';
import { useExperiment } from '@context/ExperimentContext';
import { hotelOffersTags } from '@constants/hotelOffers';
import ProductDetailsFooter from './ProductDetailsFooter';
import ProductDescription from './ProductDescription';
import TimeSlotItem from './TimeSlotItem';
import TimeSlotGuestSelector from './TimeSlotGuestSelector';

const atLeastOneAdultWarning = 'The number of adults must be greater than 0';
const errorTryAgain = "There's an error, please try again";

function TimeSlotProductDetails({
  cartData,
  date,
  hotelDetails,
  product,
  setCartData,
  setIsDrawerOpen,
}: ProductDetailsProps) {
  const { dispatch, state } = useContext(store);
  const { selectedTimeSlot, timeSlotGuestBag, pricingMarkdownExperiment } = state;

  const [adultCounter, setAdultCounter] = useState<number>(
    pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME
      ? selectedTimeSlot?.max_adults_occupancy || 0
      : 0,
  );
  const [childrenCounter, setChildrenCounter] = useState<number>(0);

  const [cookies] = useCookies();
  const { getItem } = useSessionStorage();
  const [addToCart] = useAddToCart();
  const { addOnItems, resetAddOns, updateAddonItem, onAddonUnitsSelect, showAddons } = useAddOns(
    selectedTimeSlot,
    hotelDetails,
    addonSelected,
    adultCounter,
  );
  const { algoliaAddedToCartEvent } = useAlgoliaEvents();

  const user: User = cookies.userInformation;
  const sessionID = getItem(sessionStorageKeys.SESSION_ID);
  const showFairPricing = ShouldShowFairPricingFromHotelDetails(hotelDetails);

  const resetCounters = () => {
    dispatch({
      type: actionTypes.SET_SELECTED_TIME_SLOT,
      payload: undefined,
    });

    dispatch({
      type: actionTypes.SET_TIME_SLOT_GUEST_BAG,
      payload: {
        productId: 0,
        adultsCount: 0,
        childrenCount: 0,
      },
    });
  };

  const handleFooterButtonClick = async () =>
    new Promise(async (resolve, reject) => {
      const adultsCount = adultCounter;
      const childrenCount = childrenCounter;

      if (!timeSlotGuestBag && !selectedTimeSlot) {
        reject(errorTryAgain);
      }

      if (adultsCount === 0 && childrenCount > 0) {
        toggleErrorPopup(atLeastOneAdultWarning, dispatch, true);
        reject(atLeastOneAdultWarning);
      }

      if (adultsCount > 0 && selectedTimeSlot && date) {
        try {
          const { updatedCartData } = await addToCart(
            addOnItems,
            adultsCount,
            childrenCount,
            date,
            cartData,
            selectedTimeSlot,
            hotelDetails,
            dispatch,
            user,
          );

          if (updatedCartData) {
            setCartData(updatedCartData);
          }

          /*
           * Events
           */

          // Tag Manager
          addToCartEvent(
            selectedTimeSlot,
            hotelDetails,
            addOnItems,
            adultsCount,
            childrenCount,
            updatedCartData,
            date,
          );

          algoliaAddedToCartEvent(hotelDetails, user, sessionID);

          // Reset to defaults.
          setIsDrawerOpen(false);
          resetCounters();
          resetAddOns();
          resolve(updatedCartData);
        } catch (error) {
          toggleErrorPopup(error, dispatch);
          reject(error);
        }
      }
    });

  // Reset counters on unmount
  useEffect(
    () => () => {
      resetCounters();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (selectedTimeSlot) {
      if (
        !selectedTimeSlot.individual_billing &&
        selectedTimeSlot.allow_adult &&
        selectedTimeSlot.max_adults_occupancy
      ) {
        setAdultCounter(selectedTimeSlot.max_adults_occupancy);
        if (selectedTimeSlot.max_person_occupancy !== selectedTimeSlot.max_adults_occupancy) {
          setChildrenCounter(selectedTimeSlot.max_children_occupancy);
        }
      } else {
        setAdultCounter(1);
      }
    }
  }, [selectedTimeSlot]);

  const { hotelOffersExperiment } = useExperiment();
  const showHotelOffers = hotelOffersExperiment === 'on';

  const hasProductOffer = !!product.product_offer && showHotelOffers;

  const hotelOfferTag = hotelOffersTags[product.product_offer!];

  return (
    <div className="w-full">
      <div className="h-250px w-full relative">
        <ImageLoaded url={getPhotoUrlFromProduct(product)?.url} alt="product" layout="fill" />
      </div>

      <div className="w-full px-4 pb-24">
        {/* Time slot list */}
        {date && (
          <>
            <div className="pt-5 border-b border-rp-gray-divider flex flex-col pb-6 space-y-6">
              {product.children &&
                product.children
                  .sort(
                    (a, b) =>
                      getTimeAsNumber(getDateWithSlashes(date), a.start_time) -
                      getTimeAsNumber(getDateWithSlashes(date), b.start_time),
                  )
                  .map((timeSlotProduct: Product) => (
                    <TimeSlotItem
                      showFairPricing={showFairPricing}
                      key={timeSlotProduct.product_id}
                      cartData={cartData}
                      date={date}
                      hotelDetails={hotelDetails}
                      product={timeSlotProduct}
                      parentProduct={product}
                    />
                  ))}
            </div>
            {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME &&
              selectedTimeSlot && (
                <p className="mt-6 font-rp-pn-semi-bold leading-6 text-18">Guests</p>
              )}
            {selectedTimeSlot ? (
              <TimeSlotGuestSelector
                cartData={cartData}
                date={date}
                product={selectedTimeSlot}
                hotelDetails={hotelDetails}
                showFairPricing={showFairPricing}
                adultCounter={adultCounter}
                childrenCounter={childrenCounter}
                setChildrenCounter={setChildrenCounter}
                setAdultCounter={setAdultCounter}
              />
            ) : null}
          </>
        )}

        {/* Addons */}
        {showAddons ? (
          <div className="pt-5 border-b border-rp-gray-divider flex flex-col pb-6 space-y-6" />
        ) : null}
        <Addons
          addons={addOnItems}
          onAddonUnitsSelect={onAddonUnitsSelect}
          showAddons={showAddons}
          updateAddonItem={updateAddonItem}
        />

        {hasProductOffer ? (
          <div className="flex flex-col border-b border-rp-gray-divider pb-6 mt-6">
            <div className="py-1 flex flex-row gap-1 items-center">
              <TagIconSolar width={16} height={16} color="#3164E9" />
              <span className="text-sm text-solar-brand font-rp-basetica leading-22px">
                {hotelOfferTag?.title.replace('[[AMOUNT]]', product.product_offer_amount || '')}
              </span>
            </div>
            <span className="font-rp-basetica text-sm">
              {hotelOfferTag?.description.replace('[[AMOUNT]]', product.product_offer_amount || '')}
            </span>
          </div>
        ) : null}

        {/* Product description */}
        {product.description && <ProductDescription product={product} />}
      </div>

      <div className="mt-16">
        <ProductDetailsFooter
          date={date}
          cartData={cartData}
          hotelDetails={hotelDetails}
          onButtonClick={handleFooterButtonClick}
          selectedTimeSlot={selectedTimeSlot}
          product={product}
          adultCounter={adultCounter}
        />
      </div>
    </div>
  );
}

export default TimeSlotProductDetails;
